// iter-40 — The reusable [Model & output length] panel.
//
// Single source of truth for the "Model · Output length · Conversation memory ·
// Summary bridge" control surface. Mounted by:
//   1. SiloSettings → scope="silo"      (saves to silos.{model_name, max_output_tokens, ...})
//   2. LLMRoutingPanel → scope="surface" (saves to llm_surface_tune.{surface_id} via the
//      new /api/admin/surface-tune endpoints — these travel with the LLM when it's
//      later dropped into a shelf)
//
// Tooltip placement layout (per user spec, iter-40):
//   • Top two eyes (Model, Output length)        → open down-and-to-the-right
//   • Bottom two eyes (Memory, Summary bridge)   → open up-and-to-the-right
//
// Save / Reset to defaults sit in the top-right corner of the card. Reset
// restores all four fields to the `defaults` snapshot the parent passes in.
//
// Note: when used in the future "shelves" surface, the parent passes
// `globalAuthority={false}` and the panel renders the same UI but stops
// implying it controls anything beyond the shelf it's mounted in.

import { useEffect, useState } from "react";
import { Cpu, BookmarkSimple, FloppyDisk, ArrowsCounterClockwise, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import InfoTip from "@/components/InfoTip";

const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toFixed(n < 1 ? 3 : 2)}`);

export default function ModelOutputPanel({
  // Live form values (controlled from parent).
  value = {},
  // Snapshot of defaults the Reset button restores to.
  defaults = {},
  // Provider/model registry (from /api/models/registry).
  registry,
  // Save and Reset are handled by the parent (so the parent keeps backend
  // ownership). The panel just calls them.
  onChange,
  onSave,
  // iter-40c — split resets. Either or both may be passed.
  //   onResetModel    → "Reset this LLM to Default" (top-right corner).
  //   onResetSliders  → "Reset Sliders to Default" (above the slider trio).
  // Legacy combined onResetDefaults still supported as fallback when only the
  // unified reset is wired.
  onResetDefaults,
  onResetModel,
  onResetSliders,
  // Whether this panel currently controls global silo/platform behavior.
  // Shelves will pass `false` so the heading reflects "shelf authority only".
  globalAuthority = true,
  // Disabled state during in-flight save.
  busy = false,
  // Subtitle override — shelves use a different blurb than silo settings.
  subtitle,
  // Custom heading (defaults to "Model & output length").
  heading = "Model & output length",
  // Test-id namespace so multiple panels on one page don't collide.
  scope = "silo",
}) {
  const {
    model_name = "",
    max_output_tokens = 2048,
    default_max_output_tokens = 2048,
  } = value;

  // Model-aware caps + cost lookup for the slider tooltip.
  const modelInfo = (() => {
    if (!registry?.providers) return { cap: 8192, costIn: null, costOut: null, provider: null };
    const m = model_name || registry.platform_default_model;
    for (const p of registry.providers) {
      if ((p.max_output_cap || {})[m]) {
        return {
          cap: p.max_output_cap[m],
          costIn: p.pricing_per_1m?.[m]?.in,
          costOut: p.pricing_per_1m?.[m]?.out,
          provider: p.provider,
          label: p.label,
        };
      }
    }
    return { cap: 8192, costIn: null, costOut: null, provider: null };
  })();

  // Clamp slider when model swap shrinks the cap.
  useEffect(() => {
    if (max_output_tokens > modelInfo.cap) {
      onChange?.({ ...value, max_output_tokens: modelInfo.cap });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelInfo.cap]);

  const set = (patch) => onChange?.({ ...value, ...patch });

  const setAsDefaultLength = () => {
    set({ default_max_output_tokens: max_output_tokens });
    toast.success(`Default length bookmarked at ${max_output_tokens.toLocaleString()} tokens.`);
  };
  const restoreDefaultLength = () => {
    set({ max_output_tokens: default_max_output_tokens });
    toast.success(`Reset slider to default (${default_max_output_tokens.toLocaleString()} tokens).`);
  };

  const [savingLocal, setSavingLocal] = useState(false);
  const handleSave = async () => {
    if (!onSave) return;
    setSavingLocal(true);
    try { await onSave(); }
    finally { setSavingLocal(false); }
  };

  const handleResetModel = () => {
    const fn = onResetModel || onResetDefaults;
    if (!fn) return;
    if (!window.confirm("Reset this LLM (model + provider) for this surface back to the platform default?")) return;
    fn();
    toast.success("LLM reset to default.");
  };

  const isSaving = busy || savingLocal;

  return (
    <div className="bg-white border hairline rounded-lg p-6 relative" data-testid={`mop-card-${scope}`}>
      {/* Top bar — heading on the left, "Reset this LLM to Default" + Save on the right. */}
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h3 className="font-serif-display text-xl flex items-center gap-2">
          <Cpu size={18} weight="duotone" className="text-[#4A5D4E]" /> {heading}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetModel}
            disabled={isSaving || !(onResetModel || onResetDefaults)}
            data-testid={`mop-reset-model-${scope}`}
            title="Reset this surface's model back to the platform default; sliders unchanged"
            className="text-xs px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <ArrowsCounterClockwise size={12} /> Reset this LLM to Default
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !onSave}
            data-testid={`mop-save-${scope}`}
            className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
          >
            <FloppyDisk size={12} weight="duotone" /> {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <p className="text-xs text-[#6B6A68] mb-4">
        {subtitle || (
          <>
            Switch this silo's LLM independently of others. <span className="text-[#1C1B1A]">RAG retrieval (chunk size, weights, thresholds) is model-independent</span> — swapping the model never re-tunes your retrieval.
          </>
        )}
        {!globalAuthority && (
          <span className="block mt-1 text-[10px] uppercase tracking-[0.18em] text-[#C16E5A]">
            Shelf-only authority — does not change global settings.
          </span>
        )}
      </p>

      {/* Model — TOP, eye opens DOWN-and-to-the-RIGHT. */}
      <label className="block text-sm text-[#6B6A68] mb-1.5 flex items-center gap-1.5">
        Model
        <InfoTip
          icon="eye" iconSize={14}
          width={400}
          placement="bottom-right"
          testid={`mop-info-model-${scope}`}
          label="Model trade-offs help"
        >
          Pick a model based on speed, cost, and how chatty/long-form you need answers.{"\n\n"}
          <strong>Gemini (Google)</strong>{"\n"}
          • <em>gemini-2.5-flash</em> — fastest, cheapest. ~$0.075/1M in · $0.30/1M out. Max 8,192 tokens output. Good for short factual Q&A.{"\n"}
          • <em>gemini-2.5-pro</em> / <em>3.x</em> — better reasoning + nuance. ~$1.25/1M in · $10/1M out. Same 8,192 cap.{"\n\n"}
          <strong>Anthropic Claude</strong>{"\n"}
          • <em>claude-sonnet-4.5</em> — strongest long-form prose. ~$3/1M in · $15/1M out. Cap is 64,000 tokens output (8× Gemini).{"\n"}
          • <em>claude-haiku-4.5</em> — faster, ~5× cheaper than Sonnet, still 64k output cap.{"\n\n"}
          <strong>OpenAI</strong>{"\n"}
          • <em>gpt-4o-mini</em> — ~$0.15/1M in · $0.60/1M out. Cap 16,384.{"\n"}
          • <em>gpt-5.x</em> — premium reasoning. ~$5/1M in · $15/1M out. Cap 16,384.{"\n\n"}
          The Output Length slider auto-rescales when you change the model.
        </InfoTip>
      </label>
      <div className="relative">
        <select
          data-testid={`mop-model-${scope}`}
          value={model_name}
          onChange={(e) => set({ model_name: e.target.value })}
          disabled={isSaving}
          className="appearance-none w-full bg-white border hairline rounded-md px-4 py-3 pr-10 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
        >
          <option value="">Platform default {registry?.platform_default_model ? `(${registry.platform_default_model})` : ""}</option>
          {registry?.providers?.map((p) => (
            <optgroup key={p.provider} label={p.label}>
              {p.models.map((m) => (
                <option key={m} value={m}>
                  {m}{m === p.recommended ? "  · recommended" : ""}{m === p.sentry_latest_preview && m !== p.sentry_latest_stable ? "  · preview" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {model_name && model_name !== (registry?.platform_default_model || "") && (
        <p className="text-[11px] text-[#4A5D4E] mt-1.5 flex items-center gap-1">
          <Sparkle size={10} weight="duotone" /> Overrides the platform default. Verify in QA Probe before exposing publicly.
        </p>
      )}

      {/* Output length — TOP, eye opens DOWN-and-to-the-RIGHT. */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <label className="block text-sm text-[#6B6A68] flex items-center gap-1.5">
          Max output length
          <InfoTip
            icon="eye" iconSize={14}
            width={360}
            placement="bottom-right"
            testid={`mop-info-output-${scope}`}
            label="Output length help"
          >
            How many tokens this {globalAuthority ? "silo's" : "shelf's"} AI can produce in <strong>one answer</strong>. ~1 token ≈ 0.75 words ≈ 4 characters.{"\n\n"}
            Current model: <strong>{modelInfo.label || "Platform default"}</strong>. Hard cap: <strong>{modelInfo.cap.toLocaleString()} tokens</strong>.{"\n\n"}
            Today's pricing for output (per 1M tokens): {fmtUsd(modelInfo.costOut)}{modelInfo.costOut != null ? ` — so a single ${max_output_tokens.toLocaleString()}-token answer costs about ${fmtUsd((modelInfo.costOut * max_output_tokens) / 1_000_000)}` : ""}.{"\n\n"}
            <strong>If answers are getting cut off mid-thought</strong>: push this slider up. The default 2,048 is conservative; 8k–16k is typical for chatty long-form silos.{"\n\n"}
            Trade-off: bigger answers = more output tokens billed. Use the "Set as default" button to bookmark your sweet spot and use the reset link to snap back after experimenting.
          </InfoTip>
        </label>
        <div className="flex items-center gap-2 text-xs">
          <span className="tabular-nums text-[#1C1B1A] font-medium" data-testid={`mop-tokens-value-${scope}`}>
            {max_output_tokens.toLocaleString()}<span className="text-[#6B6A68] font-normal"> tokens</span>
          </span>
        </div>
      </div>
      <input
        type="range" min="64" max={modelInfo.cap} step="64"
        value={Math.min(max_output_tokens, modelInfo.cap)}
        onChange={(e) => set({ max_output_tokens: parseInt(e.target.value, 10) })}
        disabled={isSaving}
        data-testid={`mop-tokens-slider-${scope}`}
        className="w-full mt-2 accent-[#4A5D4E]"
      />
      <div className="flex justify-between text-[10px] text-[#6B6A68] mt-1 tabular-nums">
        <span>64</span>
        <span>{Math.round(modelInfo.cap * 0.25).toLocaleString()}</span>
        <span>{Math.round(modelInfo.cap * 0.5).toLocaleString()}</span>
        <span>{Math.round(modelInfo.cap * 0.75).toLocaleString()}</span>
        <span>{modelInfo.cap.toLocaleString()}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={setAsDefaultLength}
          data-testid={`mop-set-default-tokens-${scope}`}
          className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5"
        >
          <BookmarkSimple size={12} weight="duotone" /> Set as default
        </button>
        <span className="text-[11px] text-[#6B6A68]">Bookmarks the current slider position so you can experiment and snap back.</span>
      </div>

    </div>
  );
}
